import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { useToast } from "@/components/hooks/use-toast";
import { StickySetMenuItems } from "@/components/StickySetRowControls";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { NumberInput } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MenuItem, MenuSeparator } from "@/components/ui/menu";
import { editStickyPost } from "@/lib/api";
import { removeSticky } from "@/lib/feedWrites";
import { useFeedAccessById } from "@/lib/useFeedAccess";
import { atUriToBskyUrl } from "@/lib/utils";

export function StickyPostMenuItems({
  feedId,
  stickyPostId,
  assocId,
  stickyType,
  isActive,
  postUri,
}: {
  feedId: number;
  stickyPostId: number;
  /**
   * The association row this menu belongs to. A feed can hold the same post as
   * both `pinned` and `injected`, so the list renders one entry per association
   * and `stickyPostId` alone cannot say which entry this is. Pass it wherever it
   * is known, so editing or deleting lands on this row and not its sibling.
   */
  assocId?: number;
  stickyType?: string;
  isActive: boolean;
  postUri: string;
}) {
  const { toast } = useToast();
  // `manage_sticky_posts` says "pin AND unpin". Pinning was routed through the
  // collaborator door; this was not, so the second half of the capability 403'd.
  const access = useFeedAccessById(feedId);
  const deleteStickyPostMutation = useMutation({
    mutationFn: () =>
      removeSticky({
        feedId,
        stickyPostId,
        assocId,
        stickyType,
        asCollaborator: access.isCollaborator,
      }),
    onSuccess: () =>
      toast({
        title: "Sticky post deleted",
        description: "Your sticky post has been deleted.",
      }),
    onError: (error) =>
      toast({
        title: "Error deleting sticky post",
        description: error.message,
        variant: "destructive",
      }),
  });
  const editStickyPostMutation = useMutation({
    mutationFn: async (input: { active: boolean }) => {
      if (!stickyType) {
        throw new Error("Sticky type is required");
      }

      return editStickyPost({
        algo_id: feedId,
        sticky_post_id: stickyPostId,
        assoc_id: assocId,
        is_active: input.active,
        sticky_type: stickyType,
      });
    },
    onError: (error) =>
      toast({
        title: "Error updating sticky post",
        description: error.message,
        variant: "destructive",
      }),
  });

  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const setOrderMutation = useMutation({
    onSuccess: () => {
      setOrderDialogOpen(false);
    },
    mutationFn: async (input: { order: number }) => {
      return editStickyPost({
        algo_id: feedId,
        sticky_post_id: stickyPostId,
        // This one carries no sticky_type, so without assocId an order change on
        // a doubly-stuck post would land on every copy of it.
        assoc_id: assocId,
        sticky_type: stickyType,
        order: input.order,
      });
    },
    onError: (error) =>
      toast({
        title: "Error updating sticky post",
        description: error.message,
        variant: "destructive",
      }),
  });

  return (
    <>
      {stickyType &&
        (isActive ? (
          <MenuItem
            onSelect={() => editStickyPostMutation.mutate({ active: false })}
          >
            Deactivate Sticky Post
          </MenuItem>
        ) : (
          <MenuItem
            onSelect={() => editStickyPostMutation.mutate({ active: true })}
          >
            Activate Sticky Post
          </MenuItem>
        ))}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogTrigger asChild>
          <MenuItem onSelect={(e) => e.preventDefault()}>Edit Order</MenuItem>
        </DialogTrigger>
        <DialogContent>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const form = new FormData(e.target as HTMLFormElement);
              const order = Number(form.get("order"));
              setOrderMutation.mutate({ order });
            }}
          >
            <DialogHeader>
              <DialogTitle>Edit Order</DialogTitle>
              <DialogDescription>
                Change the order of the sticky post.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <Label htmlFor="post-order">Order</Label>
              <NumberInput id="post-order" name="order" />
            </div>
            <DialogFooter>
              <Button type="submit" isLoading={setOrderMutation.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <MenuItem asChild={true}>
        <a
          className="flex items-center gap-1"
          href={atUriToBskyUrl(postUri)}
          target="_blank"
          rel="noreferrer"
        >
          View on Bluesky
        </a>
      </MenuItem>
      <MenuSeparator />
      <MenuItem onSelect={() => deleteStickyPostMutation.mutate()}>
        Delete Sticky Post
      </MenuItem>
      {/* Only renders when a sticky set put this post here. */}
      <StickySetMenuItems
        feedId={feedId}
        stickyPostId={stickyPostId}
        stickyType={stickyType}
      />
    </>
  );
}
